<template>
    <div
        class="redesign-extension-card-body relative"
        :class="{'redesign-extension-card-body--expanded' : showAllCalls}"
    >
        <div
            :class="[{'summary-container--active': showSummary && showStatistics}, {'px-4' : cardsCols <= 5}]"
            class="summary-container text-xs px-2 py-3"
        >
            <div
                v-for="(item, i) in statistic"
                :key="i"
                :class="[{'summary-row-small' : cardsCols > 5}, {'summary-row-medium': cardsCols === 5}]"
                class="summary-row grid grid-cols-2 gap-1 mb-3.5"
            >
                <div v-if="cardsCols <= 5">
                    {{ item.label }}
                </div>
                <redesign-tooltip v-else>
                    <template #tooltip-content>
                        {{ item.label }}
                    </template>
                    <div class="summary-col summary-col-label text-no-wrap">
                        {{ item.label }}
                    </div>
                </redesign-tooltip>
                <div class="grid grid-cols-3 gap-x-1">
                    <div class="summary-col">
                        <div class="flex gap-1">
                            <redesign-base-icon
                                :name="item.outgoing.icon"
                                :color="item.outgoing.iconColor"
                                class="flex-shrink-0"
                            />
                            <div>
                                {{ item.outgoing.value }}
                            </div>
                        </div>
                    </div>
                    <div class="summary-col">
                        <div class="flex gap-1">
                            <redesign-base-icon
                                :name="item.incoming.icon"
                                :color="item.incoming.iconColor"
                                class="flex-shrink-0"
                            />
                            <div>
                                {{ item.incoming.value }}
                            </div>
                        </div>
                    </div>
                    <div class="summary-col">
                        <div class="flex gap-1">
                            <redesign-base-icon
                                :name="item.other.icon"
                                :color="item.other.iconColor"
                                class="flex-shrink-0"
                            />
                            <div>
                                {{ item.other.value }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <div>
                <div v-if="calls.length > 3" class="flex text-xs gap-4 px-4 pt-2 mb-1">
                    <redesign-tag outline
                                  :tag-content="[{text: calls.length === 1 ? calls.length + ' call' : calls.length + ' calls'}]"/>
                    <redesign-toggle-button
                        @toggle="handleToggle"
                        bgColor="blue"
                        borderRadius="18px"
                        size="xs"
                        :on-label="'show All'"
                        :off-label="'show All'"
                        type="primary"
                        :round="true"
                        :disabled="false"
                    />
                </div>

                <div
                     v-if="calls.length"
                     :class="{'overflow-y-scroll': calls.length > 2}"
                     :style="{ height: showAllCalls ? '120px' : 'auto' }"
                >
                    <redesign-call-info
                        v-for="call in calls"
                        :key="call.actualDialedNumber"
                        :call="call"
                        :cards-cols="cardsCols"
                        :settings="settings"
                        class="px-4 flex number-content items-center justify-between py-2"
                    >
                    </redesign-call-info>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed} from 'vue';
import RedesignTag from "@/components/RedesignBaseComponents/RedesignTag.vue";
import RedesignBaseIcon from "@/components/RedesignBaseComponents/RedesignBaseIcon.vue";
import RedesignToggleButton from "@/components/RedesignBaseComponents/RedesignToggleButton.vue";
import RedesignCallInfo from "@/components/RedesignComponents/Cards/ExtensionCard/RedesignCallInfo.vue";
import {redesignCallTypeIcons} from "@/enum/colors";
import {useExtensionCardInject} from "@/composables/useExtensionCardProvideInject";
import i18n from "@/i18n";
import {watch} from "vue";
import RedesignTooltip from "@/components/RedesignBaseComponents/RedesignTooltip.vue";
import get from "lodash/get";
import {realTimeSettings} from "@/enum/defaultWidgetSettings";

const extension = useExtensionCardInject()

const props = defineProps({
    showSummary: {
        type: Boolean,
        default: false,
    },
    settings: {
        type: Object,
        default: () => ({})
    },
    showAllCalls: {
        type: Boolean,
        default: false,
    },
    cardsCols: Number,
});

const emit = defineEmits(['toggle']);

const showStatistics = computed(() => {
    return !props.settings.hideStatistics
})
const calls = computed(() => {
    return extension.value.calls || []
})

const createTagData = (label = '', outgoingVal, incomingVal, othersVal) => {
    return {
        label,
        outgoing: {
            icon: redesignCallTypeIcons.outgoing,
            iconColor: redesignCallTypeIcons.outgoingColor,
            value: String(outgoingVal)
        },
        incoming: {
            icon: redesignCallTypeIcons.incoming,
            iconColor: redesignCallTypeIcons.incomingColor,
            value: String(incomingVal)
        },
        other: {
            icon: redesignCallTypeIcons.otherCalls,
            iconColor: redesignCallTypeIcons.otherColor,
            value: String(othersVal)
        }
    }
}
const statistic = computed(() => {
    const extensionStatistic = extension.value.allDataSummary

    if (extensionStatistic) {
        const calls = createTagData(i18n.t('widget.total.calls'), extensionStatistic.OutgouingCallsCount, extensionStatistic.IncomingCallsCount, extensionStatistic.OtherCallsCount)
        const totalTime = createTagData(i18n.t('statistic.total.time'), extensionStatistic.OutgouingCallsSum, extensionStatistic.IncomingCallsSum, extensionStatistic.OtherCallsSum)
        const averageDuration = createTagData(i18n.t('statistics.avg.call.duration'), extensionStatistic.OutgouingCallsAvg, extensionStatistic.IncomingCallsAVG, extensionStatistic.OtherCallsAvg)

        const rate = {
            label: i18n.t('statistic.avg.ratio'),
            incoming: {icon: '', value: ''},
            outgoing: {icon: '', value: extensionStatistic.GrossPercentageOfEmployment + ' %'},
            other: {icon: '', value: ''}

        }

        return [calls, totalTime, averageDuration, rate]
    }
    return []

})

const handleToggle = (val) => {
    emit('toggle', val);
};

watch(() => calls.value.length, (value) => {
    if (!value && props.showAllCalls) {
        handleToggle(false)
    }
})
</script>

<style lang="scss">
.redesign-extension-card-body {
    background-color: var(--mainBgColor-color);
    min-height: 150px;

    .text-no-wrap {
        text-wrap: nowrap;
        text-overflow: ellipsis;
        width: 100%;
        overflow: hidden;
    }

    .summary-row {
        @apply grid grid-cols-2;
    }

    .summary-row-medium {
        grid-template-columns: 35% 65%;
    }

    .summary-row-small {
        grid-template-columns: 23% 77%;
    }
}

.summary-container {
    background-color: var(--cardsBgColor-color);
    color: var(--mainTextColor-color);
    position: absolute;
    z-index: -1;
    width: 100%;
    height: 400px;
    top: 150%;
    transition: all 1s;

    &--active {
        z-index: 5;
        top: 0;
    }
}
</style>
